/**
 * Admin → Rate cards (the NAMED containers).
 *
 * Per ADR-008, each card has a routing strategy (`lcr` | `lcd` | `priority`)
 * that tiebreaks between competing rates. Cards are attached to tenants
 * via the tenant_rate_cards junction (see useAdminTenantRateCards).
 *
 * The items inside each card live in useAdminRates with cardId param.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type RateCardStrategy = 'lcr' | 'lcd' | 'priority';

/**
 * 'platform' = wholesale card the platform admin uses to bill resellers.
 * 'reseller' = retail card a reseller uses to bill its customers.
 *
 * The dispatcher reads cost from a platform card (via tenants.wholesale_
 * card_id) and price from the customer's assigned reseller card. Cards
 * created by a reseller-tenant default to 'reseller'; cards created by
 * the platform tenant are auto-marked 'platform' (PR 2 of the billing
 * sequence).
 */
export type RateCardOwnerType = 'platform' | 'reseller';

/**
 * 'wholesale' = the dispatcher reads buy_cost from this card to bill
 * the reseller (via tenants.wholesale_card_id). 'retail' = the
 * customer-side resolver reads sell_price from this card to bill the
 * customer.
 *
 * Orthogonal to owner_type — today they're correlated (platform-owned
 * ↔ wholesale, reseller-owned ↔ retail) but a future sub-reseller
 * tier breaks that. Use card_type for "what's this card for"
 * decisions; use owner_type only for "who can edit this".
 */
export type RateCardType = 'wholesale' | 'retail';

export interface RateCard {
  id: number;
  reseller_id: number;
  name: string;
  description?: string;
  is_default: boolean;
  strategy: RateCardStrategy;
  owner_type: RateCardOwnerType;
  card_type: RateCardType;
  rate_count: number;
  carrier_count: number;
  tenant_count: number;
  created_at: string;
  updated_at: string;
}

export interface RateCardsResponse {
  items: RateCard[];
}

export interface CreateRateCardInput {
  name: string;
  description?: string;
  is_default?: boolean;
  strategy?: RateCardStrategy;
}

export interface UpdateRateCardInput {
  name: string;
  description?: string;
  strategy: RateCardStrategy;
}

const RATE_CARDS_KEY = ['admin', 'rate-cards'] as const;
const RATE_CARD_KEY = (id: number) => ['admin', 'rate-cards', id] as const;

export function useRateCards() {
  return useQuery({
    queryKey: RATE_CARDS_KEY,
    queryFn: () => api.get<RateCardsResponse>('/v1/admin/rate-cards'),
    staleTime: 30_000,
  });
}

export function useRateCard(id: number) {
  return useQuery({
    queryKey: RATE_CARD_KEY(id),
    queryFn: () => api.get<RateCard>(`/v1/admin/rate-cards/${id}`),
    staleTime: 30_000,
    enabled: id > 0,
  });
}

export function useCreateRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRateCardInput) =>
      api.post<RateCard>('/v1/admin/rate-cards', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RATE_CARDS_KEY });
    },
  });
}

export function useUpdateRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateRateCardInput }) =>
      api.put<RateCard>(`/v1/admin/rate-cards/${id}`, input),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: RATE_CARDS_KEY });
      void qc.invalidateQueries({ queryKey: RATE_CARD_KEY(id) });
    },
  });
}

export function useSetDefaultRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<RateCard>(`/v1/admin/rate-cards/${id}/default`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RATE_CARDS_KEY });
    },
  });
}

export function useDeleteRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<void>(`/v1/admin/rate-cards/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RATE_CARDS_KEY });
    },
  });
}
